import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ScannerCaseFilter, ScannerCaseOrderStatus, ScanSource } from 'models/scanner-case';

type ScannerCasesPageActionsProps = {
    onFilterChange: (filters: ScannerCaseFilter) => void;
    initFilters: ScannerCaseFilter;
};
const ScannerCasesPageActions: FC<ScannerCasesPageActionsProps> = ({ onFilterChange, initFilters }) => {
    const { t: translate } = useTranslation(['scan-cases', 'common']);
    const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout>>();
    const [search, setSearch] = useState(initFilters.search || '');
    const [status, setStatus] = useState<FilterScannerStatus>(FilterScannerStatus.NEW);
    const [source, setSource] = useState<FilterScannerSource>(FilterScannerSource.ALL);

    useEffect(() => {
        setSearch(initFilters.search || '');
        if (initFilters.source) {
            setSource(FilterScannerSource[initFilters.source]);
        }
        if (initFilters.orderStatus) {
            setStatus(FilterScannerStatus[initFilters.orderStatus]);
        }
        if (initFilters.search) {
            setSearch(initFilters.search);
        }
    }, [initFilters]);

    const emitChange = (search: string, orderStatus?: ScannerCaseOrderStatus, source?: ScanSource) => {
        const filters = {
            search,
            orderStatus,
            source,
        };
        onFilterChange(filters);
    };

    const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearch(value);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        const timeout = setTimeout(() => {
            if (value.length === 0 || value.length >= 3) {
                emitChange(
                    value,
                    status === FilterScannerStatus.ALL ? undefined : (status as unknown as ScannerCaseOrderStatus),
                    source === FilterScannerSource.ALL ? undefined : (source as unknown as ScanSource),
                );
            }
        }, 300);
        setSearchTimeout(timeout);
    };

    const handleStatusChange = (event: SelectChangeEvent) => {
        const value = event.target.value as FilterScannerStatus;
        setStatus(value);
        if (value === FilterScannerStatus.ALL) {
            emitChange(
                search,
                undefined,
                source === FilterScannerSource.ALL ? undefined : (source as unknown as ScanSource),
            );
            return;
        }
        emitChange(
            search,
            value as unknown as ScannerCaseOrderStatus,
            source === FilterScannerSource.ALL ? undefined : (source as unknown as ScanSource),
        );
    };

    const handleSourceChange = (event: SelectChangeEvent) => {
        const value = event.target.value as FilterScannerSource;
        setSource(value);
        if (value === FilterScannerSource.ALL) {
            emitChange(
                search,
                status === FilterScannerStatus.ALL ? undefined : (status as unknown as ScannerCaseOrderStatus),
                undefined,
            );
            return;
        }
        emitChange(
            search,
            status === FilterScannerStatus.ALL ? undefined : (status as unknown as ScannerCaseOrderStatus),
            value as unknown as ScanSource,
        );
    };

    return (
        <Grid container rowSpacing={1.5} columnSpacing={1}>
            <Grid item md={5} sm={6} xs={12}>
                <TextField
                    fullWidth
                    onChange={handleTextChange}
                    label={translate('scan-cases:FREE_TEXT_FILTER')}
                    placeholder={translate('scan-cases:FREE_TEXT_FILTER_PLACEHOLDER')}
                    value={search}
                />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
                <FormControl fullWidth>
                    <InputLabel id="status-select-label">{translate('common:STATUS')}</InputLabel>
                    <Select
                        value={status}
                        onChange={handleStatusChange}
                        labelId="status-select-label"
                        label={translate('common:STATUS')}
                    >
                        {Object.values(ScannerCaseOrderStatus).map((status, index) => {
                            return (
                                <MenuItem key={index} value={status}>
                                    {translate(`scan-cases:${status}`)}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
                <FormControl fullWidth>
                    <InputLabel id="source-select-label">{translate('scan-cases:SOURCE')}</InputLabel>
                    <Select
                        value={source}
                        onChange={handleSourceChange}
                        labelId="source-select-label"
                        label={translate('scan-cases:SOURCE')}
                    >
                        {Object.values(FilterScannerSource).map((source, index) => {
                            return (
                                <MenuItem key={index} value={source}>
                                    {translate(`scan-cases:${source}`)}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    );
};

enum FilterScannerSource {
    ALL = 'ALL',
    THREE_SHAPE = 'THREE_SHAPE',
    DEXIS = 'DEXIS',
    DS_CORE = 'DS_CORE',
    MEDIT = 'MEDIT',
    PANDA = 'PANDA',
    PANDA_EU = 'PANDA_EU',
}

enum FilterScannerStatus {
    ALL = 'ALL',
    NEW = 'NEW',
    ORDER_CREATED = 'ORDER_CREATED',
    DO_NOT_IMPORT = 'DO_NOT_IMPORT',
}

export default ScannerCasesPageActions;
